import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Icon } from '@repo/ui';

export default function QboCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing QuickBooks Online authorization...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const realmId = searchParams.get('realmId');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors from QuickBooks Online
        if (error) {
          setStatus('error');
          setMessage(errorDescription || `Authorization failed: ${error}`);

          // Send error message to parent window
          window.opener?.postMessage(
            {
              type: 'QBO_AUTH_ERROR',
              error: errorDescription || error,
            },
            window.location.origin
          );

          // Close window after delay
          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        // Handle missing required parameters
        if (!code || !state || !realmId) {
          setStatus('error');
          setMessage('Invalid authorization response - missing required parameters');

          window.opener?.postMessage(
            {
              type: 'QBO_AUTH_ERROR',
              error: 'Invalid authorization response - missing code, state, or realmId',
            },
            window.location.origin
          );

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        // Success - send the code, state, and realmId back to parent window
        setStatus('success');
        setMessage('Authorization successful! Closing window...');

        window.opener?.postMessage(
          {
            type: 'QBO_AUTH_SUCCESS',
            code,
            state,
            realmId,
          },
          window.location.origin
        );

        // Close window after showing success
        setTimeout(() => {
          window.close();
        }, 2000);
      } catch (error) {
        console.error('QuickBooks Online OAuth callback error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Authorization failed');

        window.opener?.postMessage(
          {
            type: 'QBO_AUTH_ERROR',
            error: error instanceof Error ? error.message : 'Authorization failed',
          },
          window.location.origin
        );

        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Icon name="HiRefresh" size="2xl" className="text-xtnd-blue animate-spin" />;
      case 'success':
        return <Icon name="HiCheckCircle" size="2xl" className="text-green-500" />;
      case 'error':
        return <Icon name="HiXCircle" size="2xl" className="text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-xtnd-blue';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-xtnd-light to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-xtnd-blue rounded-full mb-4">
            <svg
              className="h-8 w-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-xtnd-dark mb-2">QuickBooks Online Integration</h1>
          <p className="text-xtnd-dark-500 text-sm">Connecting to your QuickBooks Online account</p>
        </div>

        {/* Status Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">{getStatusIcon()}</div>

          <h2 className={`text-xl font-semibold mb-4 ${getStatusColor()}`}>
            {status === 'processing' && 'Processing Authorization'}
            {status === 'success' && 'Connection Successful!'}
            {status === 'error' && 'Connection Failed'}
          </h2>

          <p className="text-xtnd-dark-500 mb-6">{message}</p>

          {status === 'processing' && (
            <div className="flex justify-center">
              <div className="animate-pulse flex space-x-1">
                <div className="w-2 h-2 bg-xtnd-blue rounded-full"></div>
                <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-200"></div>
                <div className="w-2 h-2 bg-xtnd-blue rounded-full animation-delay-400"></div>
              </div>
            </div>
          )}

          {status === 'success' && (
            <p className="text-sm text-green-600">This window will close automatically...</p>
          )}

          {status === 'error' && (
            <div className="space-y-2">
              <p className="text-sm text-xtnd-dark-400">This window will close automatically.</p>
              <button
                onClick={() => window.close()}
                className="text-sm text-xtnd-blue hover:text-xtnd-dark underline"
              >
                Close window now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
